import { useMemo } from 'react';

import { useParams } from 'react-router-dom';

import { useGetOpportunity } from '~/hooks/apis/offers/useGetOffer';

import { usePricingToolSession } from '../PricingToolSessionContext';
import { PRICING_OPTION_LABELS } from '../constants';
import { computeForecastTotals } from '../tabs/forecasting/computeForecastTotals';
import type { OverrideCommissionTier } from '../tabs/forecasting/computeOverrideCommission';
import {
  DEFAULT_SOLO_RATE_PCT,
  DEFAULT_UPGRADE_RATE_PCT,
  EMPTY_OPTION_INPUTS,
  parseMoney,
  parsePercentWithDefault,
  resolveEffectivePackages,
} from '../tabs/forecasting/forecastingModel';
import { sumInclusionValue } from '../tabs/inclusions/inclusionsModel';
import {
  computeOptionDiscountStats,
  computeOptionPricingSummary,
  leadOf,
  scrapeStayFor,
} from '../tabs/pricingSummary/pricingSummaryModel';
import { computeDailyMarketStats } from '../tabs/scrape/otaParity/computeOtaMarketStats';
import { computeOtaSummaryStats } from '../tabs/scrape/otaParity/computeSummaryStats';
import { computeStaySurchargeByDate } from '../tabs/surcharges/computeStaySurcharge';

import type { CommercialApprovalData, CommercialApprovalPrvChartPoint, CommercialApprovalPrvMonthRow } from './types';

// Mirrors the mockup's Discount input (derives Indicative Price from Avg OTA); the real tab has
// no such control yet, so this is a fixed default rather than fabricated data.
const DEFAULT_PRV_MONTH_DISCOUNT_PCT = 20;

/**
 * Snapshots the current Pricing Tool session into the shared content model consumed by both
 * the Commercial Approval HTML and PDF exports, scoped to the selected preferred option. This
 * is an exec-facing overview (mirrors the mockup's own Commercial Approval scope), not a full
 * pricing breakdown - it deliberately omits room-by-room package pricing and surcharge detail.
 * Returns null until a preferred option is chosen, mirroring the mockup's export guard.
 */
export function useCommercialApprovalData(): CommercialApprovalData | null {
  const { id_offer: offerId } = useParams<{ id_offer: string }>();
  const {
    propertySetup,
    packageOptions,
    inclusions,
    blackouts,
    overrides,
    scrape,
    surcharges,
    forecasting,
    preferredOption,
  } = usePricingToolSession();

  const { data: opportunity } = useGetOpportunity<App.OpportunityPage>(offerId, { enabled: !!offerId });
  const { currency: netCurrency, rate: exchangeRate, audToSourceCurrencyRate } = propertySetup.fx;

  return useMemo(() => {
    if (preferredOption === null) {
      return null;
    }

    const optionOverrides = overrides.overrides.options[preferredOption];
    const optionState = packageOptions.optionStates[preferredOption];
    if (!optionState) {
      return null;
    }

    const optionInclusionDrafts = inclusions.drafts.filter((draft) => draft.pricingOptionIndex === preferredOption);
    const inclusionsTotalAud = sumInclusionValue(optionInclusionDrafts);

    // ---- Pricing Summary (XTRNT-1007): Lead Sell Price, PRV Chart, PRV by Month ----
    const { stayInputs, parsedCsv } = scrape;
    const marketStats = parsedCsv ? computeDailyMarketStats(parsedCsv, undefined, stayInputs?.blackoutDates) : [];
    const lead = leadOf(optionState);
    const stay = scrapeStayFor(lead, stayInputs);
    const surchargeByDate = stay
      ? computeStaySurchargeByDate(
          marketStats.map((stats) => stats.date),
          stay.nights,
          surcharges.session.grids,
          { optionIndex: preferredOption, roomId: lead?.roomAssetId },
        )
      : {};
    const discountStats = computeOptionDiscountStats(
      marketStats,
      stay,
      lead?.sell ?? null,
      inclusionsTotalAud,
      surchargeByDate,
    );
    const pricingSummary = computeOptionPricingSummary({
      optionIndex: preferredOption,
      option: optionState,
      stay,
      parsedCsv,
      marketStats,
      discountStats,
      netCurrency,
      audToSourceCurrencyRate,
    });
    const otaSummary =
      parsedCsv && stay ? computeOtaSummaryStats(parsedCsv, marketStats, discountStats, stay.nights) : null;
    const genuinePrvValues = discountStats
      .map((stats) => stats.genDiscountPct)
      .filter((value): value is number => value != null);
    const below30PrvCount = genuinePrvValues.filter((value) => value < 30).length;
    const negativePrvCount = genuinePrvValues.filter((value) => value < 0).length;
    const prvSampleTotal = genuinePrvValues.length || null;

    const leadSellPrice = {
      sellAud: pricingSummary.sell,
      netRate: pricingSummary.net,
      marginPct: pricingSummary.marginPct,
      avgPrv: otaSummary?.weightedGenDiscountPct.valuePct ?? pricingSummary.prvPct,
      minPrv: otaSummary?.genDiscountQuartiles.q1 ?? null,
      maxPrv: otaSummary?.genDiscountQuartiles.q3 ?? null,
      avgSellVsMinOtaPct: otaSummary?.weightedSellVsMinOtaPct.valuePct ?? null,
      below30PrvCount: prvSampleTotal != null ? below30PrvCount : null,
      below30PrvTotal: prvSampleTotal,
      negativePrvCount: prvSampleTotal != null ? negativePrvCount : null,
      negativePrvTotal: prvSampleTotal,
    };

    const prvChart: Array<CommercialApprovalPrvChartPoint> = pricingSummary.monthRows.map((row) => ({
      month: row.label,
      avgOta: row.avgOta,
      leSell: row.leSell,
      medianPrvPct: row.medianPrvPct,
    }));
    const prvByMonth: Array<CommercialApprovalPrvMonthRow> = pricingSummary.monthRows.map((row) => ({
      month: row.label,
      medianPrvPct: row.medianPrvPct,
      avgOta: row.avgOta,
      lowOta: row.lowOta,
      indicativePrice: row.avgOta != null ? Math.round(row.avgOta * (1 - DEFAULT_PRV_MONTH_DISCOUNT_PCT / 100)) : null,
      leSell: row.leSell,
    }));

    // ---- Forecasting (XTRNT-1013): Forecast Summary + Deal Info's forecast fields ----
    const audToContractRate = exchangeRate !== undefined && exchangeRate > 0 ? exchangeRate : undefined;
    const firstOptionInputs = forecasting.session.optionInputs[0] ?? EMPTY_OPTION_INPUTS;
    const optionInputs = forecasting.session.optionInputs[preferredOption] ?? EMPTY_OPTION_INPUTS;
    const effectivePackages = resolveEffectivePackages(optionInputs, preferredOption, firstOptionInputs);
    const commissionTiers: Array<OverrideCommissionTier> = optionOverrides.tiers
      .filter((tier): tier is typeof tier & { to: number } => tier.to != null)
      .map((tier) => ({ from: tier.from, to: tier.to, commissionPct: tier.comms ?? 0 }));

    const forecastTotals =
      audToContractRate === undefined
        ? null
        : computeForecastTotals({
            audToContractRate,
            expectedAvgLos: optionInputs.expectedAvgLos.trim() === '' ? null : parseMoney(optionInputs.expectedAvgLos),
            lengthsOfStay: optionState.lengthsOfStay,
            packagesByAssetLos: effectivePackages,
            pricingByRoomId: optionState.pricingByRoomId,
            roomsByCategory: optionState.roomsByCategory,
            soloRatePct: parsePercentWithDefault(optionInputs.soloRatePct, DEFAULT_SOLO_RATE_PCT),
            upgradeRatePct: parsePercentWithDefault(optionInputs.upgradeRatePct, DEFAULT_UPGRADE_RATE_PCT),
            commissionBasis: optionOverrides.basis === 'vendor_revenue' ? 'vendorRevenue' : 'roomNights',
            tiers: commissionTiers,
          });

    const forecastSummary = {
      packages: forecastTotals?.packages ?? null,
      roomNights: forecastTotals?.roomNights ?? null,
      avgLos: forecastTotals?.avgLos ?? null,
      blendedAdrAud: forecastTotals?.blendedAdrAud ?? null,
      ttvAud: forecastTotals?.ttvAud ?? null,
      vendorRevenueAud: forecastTotals?.vendorRevenueAud ?? null,
      bundledExperienceCostAud: forecastTotals?.bundledCostAud ?? null,
      overrideCommsPct: forecastTotals?.overrideCommsPct || null,
      overrideCommsAud: forecastTotals?.overrideCommsAud || null,
      grossMarginAud: forecastTotals?.grossMarginAud ?? null,
      marginPct: forecastTotals?.pctMargin ?? null,
    };

    // ---- Deal Information: real Opportunity fields (Deal Info -> Details tab) + forecast ----
    const dealInformation = {
      salesForecastAud: forecastTotals?.ttvAud ?? null,
      marginForecastAud: forecastTotals?.grossMarginAud ?? null,
      tier: opportunity?.key_commercial?.deal_tier__c ?? null,
      weighting: opportunity?.sales_and_forecast?.weighting__c ?? null,
      offlineRunPeriod: opportunity?.run_dates?.offline_run_period__c ?? null,
      paymentMethod: opportunity?.contract?.payment_method__c ?? null,
    };

    // ---- Previous Campaign Performance: only 3 of 10 fields have any real source ----
    const previousCampaign = {
      ttvAud: opportunity?.sales_and_forecast?.previous_ttv__c ?? null,
      marginAud: null,
      marginPct: null,
      roomNights: null,
      packages: null,
      adr: null,
      avgLeadTime: null,
      avgLos: null,
      countriesNotToRun: opportunity?.key_commercial?.countries_not_to_run__c?.join(', ') || null,
      brandNotRunning: opportunity?.key_commercial?.brand_not_running__c?.join(', ') || null,
    };

    return {
      propertyName: propertySetup.values?.propertyName ?? 'Property Pricing',
      offerCountry: propertySetup.values?.offerCountry ?? null,
      optionName: PRICING_OPTION_LABELS[preferredOption],
      lengthsOfStay: optionState.lengthsOfStay,
      currency: netCurrency,
      exportDate: new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }),
      inclusions: optionInclusionDrafts.map((draft) => ({
        name: draft.name,
        type: draft.type,
        applies: draft.applies,
        prv: draft.prv,
      })),
      inclusionsTotalAud,
      blackouts: blackouts.blackouts.map((blackout) => ({
        name: blackout.name,
        startDate: blackout.startDate,
        endDate: blackout.endDate,
      })),
      overrides: {
        basis: optionOverrides.basis,
        tiers: optionOverrides.tiers,
      },
      dealInformation,
      previousCampaign,
      forecastSummary,
      leadSellPrice,
      prvChart,
      prvByMonth,
      prvByMonthDiscountPct: DEFAULT_PRV_MONTH_DISCOUNT_PCT,
      isPlaceholderData: {
        dealInformation: !opportunity,
        previousCampaign: true,
        forecastSummary: !forecastTotals,
        leadSellPrice: !stay || !parsedCsv,
        prvChart: prvChart.length === 0,
        prvByMonth: prvByMonth.length === 0,
      },
    };
  }, [
    propertySetup.values,
    packageOptions.optionStates,
    inclusions.drafts,
    blackouts.blackouts,
    overrides.overrides,
    scrape,
    surcharges.session,
    forecasting.session,
    opportunity,
    netCurrency,
    audToSourceCurrencyRate,
    exchangeRate,
    preferredOption,
  ]);
}
